<template>
  <TransitionRoot as="template" :show="sidebarOpen">
    <Dialog
      as="div"
      class="fixed inset-0 flex z-40 md:hidden"
      @close="sidebarOpen = false"
    >
      <TransitionChild
        as="template"
        enter="transition-opacity ease-linear duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="transition-opacity ease-linear duration-300"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-gray-600 bg-opacity-75" />
      </TransitionChild>
      <TransitionChild
        as="template"
        enter="transition ease-in-out duration-300 transform"
        enter-from="-translate-x-full"
        enter-to="translate-x-0"
        leave="transition ease-in-out duration-300 transform"
        leave-from="translate-x-0"
        leave-to="-translate-x-full"
      >
        <div
          class="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-indigo-700 dark:bg-gray-800"
        >
          <TransitionChild
            as="template"
            enter="ease-in-out duration-300"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="ease-in-out duration-300"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <div class="absolute top-0 right-0 -mr-12 pt-2">
              <button
                type="button"
                class="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                @click="sidebarOpen = false"
              >
                <span class="sr-only">Close sidebar</span>
                <XIcon class="h-6 w-6 text-white" aria-hidden="true" />
              </button>
            </div>
          </TransitionChild>
          <div class="flex-shrink-0 flex items-center px-4">
            <AppLogo />
          </div>
          <div class="mt-5 flex-1 h-0 overflow-y-auto">
            <nav class="px-2 space-y-1">
              <Link
                v-for="item in navigation"
                :key="item.name"
                :href="item.href"
                :class="[
                  item.current
                    ? 'bg-indigo-800 dark:bg-gray-900 text-white'
                    : 'text-indigo-100 hover:bg-indigo-600 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white',
                  'group flex items-center px-2 py-2 text-base font-medium rounded-md',
                ]"
              >
                <component
                  :is="item.icon"
                  class="mr-4 flex-shrink-0 h-6 w-6 text-indigo-300 dark:text-gray-300"
                  aria-hidden="true"
                />
                {{ item.name }}
              </Link>
            </nav>
          </div>
        </div>
      </TransitionChild>
      <div class="flex-shrink-0 w-14" aria-hidden="true">
        <!-- Dummy element to force sidebar to shrink to fit close icon -->
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Static sidebar for desktop -->
  <div class="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
    <!-- Sidebar component, swap this element with another sidebar if you like -->
    <div class="flex-1 flex flex-col min-h-0 bg-indigo-700 dark:bg-gray-800">
      <!-- This container is what puts the logo at the bottom-->
      <div class="flex flex-col justify-between flex-grow">
        <div class="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
          <div class="flex items-center flex-shrink-0 px-4">
            <AppLogo />
          </div>
          <nav class="mt-5 flex-1 px-2 space-y-1" aria-label="Sidebar">
            <Link
              v-for="item in navigation"
              :key="item.name"
              :href="item.href"
              :class="[
                item.current
                  ? 'bg-indigo-800 text-white dark:bg-gray-900'
                  : 'text-indigo-100 hover:bg-indigo-600 hover:bg-opacity-75 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white',
                'group flex items-center px-2 py-2 text-sm font-medium rounded-md',
              ]"
            >
              <component
                :is="item.icon"
                class="mr-3 flex-shrink-0 h-6 w-6 text-indigo-300 dark:text-gray-300"
                aria-hidden="true"
              />
              <span class="flex-1">
                {{ item.name }}
              </span>
              <span
                v-if="item.count"
                :class="[
                  item.current
                    ? 'bg-indigo-600 dark:bg-gray-500'
                    : 'bg-indigo-800 dark:bg-gray-600',
                  'ml-3 inline-block py-0.5 px-3 text-xs font-medium rounded-full',
                ]"
              >
                {{ item.count }}
              </span>
            </Link>
          </nav>
        </div>
        <div class="mb-4 flex justify-center">
          <a href="https://jetphix.com" target="_blank">
            <img
              class="h-10 hover:brightness-50 hover:shadow-red-500 invert"
              src="/img/jetphix-logo-black-transparent.png"
          /></a>
        </div>
      </div>
    </div>
  </div>
  <div class="md:pl-64 flex flex-col flex-1">
    <div
      class="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white shadow dark:bg-gray-700"
    >
      <button
        type="button"
        class="px-4 border-r border-gray-200 dark:border-gray-800 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 md:hidden dark:text-gray-200"
        @click="sidebarOpen = true"
      >
        <span class="sr-only">Open sidebar</span>
        <MenuAlt2Icon class="h-6 w-6" aria-hidden="true" />
      </button>
      <div class="flex-1 px-4 flex justify-between">
        <div class="flex-1 flex">
          <!-- Search was here -->
        </div>
        <div class="ml-4 flex items-center md:ml-6">
          <!-- <button
            type="button"
            class="bg-white p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200 dark:hover:text-white"
          >
            <span class="sr-only">View notifications</span>
            <BellIcon class="h-6 w-6" aria-hidden="true" />
          </button> -->

          <!-- Profile dropdown -->
          <Menu as="div" class="ml-3 relative" v-if="useUser">
            <div>
              <MenuButton
                class="max-w-xs bg-white flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 lg:p-2 lg:rounded-md lg:hover:bg-gray-50 dark:bg-gray-700 dark:lg:hover:bg-gray-600"
              >
                <span class="sr-only">Open user menu</span>
                <AvatarUser :user="useUser" size="sm" />
                <span
                  class="hidden ml-3 text-gray-700 dark:text-gray-200 text-sm font-medium lg:block"
                  ><span class="sr-only">Open user menu for </span
                  >{{ useUser.name }}</span
                >
                <ChevronDownIcon
                  class="hidden flex-shrink-0 ml-1 h-5 w-5 text-gray-400 lg:block"
                />
              </MenuButton>
            </div>
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <MenuItems
                class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
              >
                <MenuItem
                  v-for="item in userNavigation"
                  :key="item.name"
                  v-slot="{ active }"
                >
                  <Link
                    :href="item.href"
                    :method="item.method ? item.method : 'get'"
                    :as="item.as ? item.as : 'a'"
                    :class="[
                      active ? 'bg-gray-100' : '',
                      'block px-4 py-2 text-sm text-gray-700 w-full text-left',
                    ]"
                    >{{ item.name }}</Link
                  >
                </MenuItem>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>
    </div>

    <main class="py-6">
      <div class="px-4 sm:px-6 max-w-7xl mx-auto lg:px-8">
        <PageHeadingText v-if="props.title" :title="props.title" />
        <slot name="header"></slot>
        <FlashMessage />
      </div>
      <div
        class="mx-auto sm:px-6 md:px-8 max-w-7xl"
        :class="{ 'mt-6': !!$slots.header || props.title }"
      >
        <slot></slot>
      </div>
    </main>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { Link } from '@inertiajs/inertia-vue3'
import {
  Dialog,
  DialogOverlay,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import {
  BellIcon,
  CalendarIcon,
  GlobeAltIcon,
  HomeIcon,
  PaperAirplaneIcon,
  MenuAlt2Icon,
  XIcon,
} from '@heroicons/vue/outline'
import { ChevronDownIcon } from '@heroicons/vue/solid'
import { useUser } from '@/Util/UseUser'
import AppLogo from './AppLogo.vue'
import PageHeadingText from '@/Common/Headings/PageHeadings/Parts/PageHeadingText.vue'
import FlashMessage from '@/Common/Feedback/Alerts/FlashMessage.vue'
import AvatarUser from '@/Common/Elements/Avatars/AvatarUser.vue'

const props = defineProps({
  title: String,
})

// Admins manage every plane, so they always get the full list
const onlyPlane = computed(() =>
  useUser.value?.planes?.length === 1 && !useUser.value?.is_admin
    ? useUser.value.planes[0]
    : null
)

const navigation = [
  {
    name: 'Home',
    icon: HomeIcon,
    href: route('home'),
    current: route().current('home'),
  },
  {
    name: 'Calendar',
    icon: CalendarIcon,
    href: route('calendar.index'),
    current: route().current('calendar.*'),
  },
  {
    name: 'Trips',
    icon: GlobeAltIcon,
    href: route('trips.index'),
    count: 0,
    current: route().current('trips.*'),
  },
  {
    name: onlyPlane.value ? onlyPlane.value.tail_number : 'Planes',
    icon: PaperAirplaneIcon,
    href: onlyPlane.value
      ? route('planes.show', onlyPlane.value)
      : route('planes.index'),
    count: 0,
    current: route().current('planes.*'),
  },
]
const userNavigation = [
  { name: 'Settings', href: route('settings.index') },
  { name: 'Sign out', href: route('logout'), method: 'post', as: 'button' },
]

const sidebarOpen = ref(false)
</script>
